import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import {
    getFirestore,
    initializeFirestore,
    persistentLocalCache,
    persistentMultipleTabManager
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getAnalytics } from 'firebase/analytics';

const projectEnv = import.meta.env.VITE_FIREBASE_PROJECT || 'tas-ski-stg';
const isStaging = projectEnv === 'tas-ski-stg';

// Firebase configuration - Update these values from your Firebase console
const firebaseConfig = isStaging ? {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyAwzWocBRbTSemSAN6s5g5FilX-k_mEh0I",
    authDomain: "tas-ski-stg.firebaseapp.com",
    projectId: "tas-ski-stg",
    storageBucket: "tas-ski-stg.firebasestorage.app",
    messagingSenderId: "570721188758",
    appId: "1:570721188758:web:82a104080ae8c3d9067bb7",
    measurementId: "G-E0YC62XS94"
} : {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyDtIAEMCtO7WatOratlMERgV5acG0ZmysQ",
    authDomain: "tas-ski.firebaseapp.com",
    projectId: "tas-ski",
    storageBucket: "tas-ski.firebasestorage.app",
    messagingSenderId: "758583924198",
    appId: "1:758583924198:web:6841ea4b01ca7f7ff6b227",
    measurementId: "G-XE1WKWKXE4"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

/**
 * Detects whether the client is a mobile device or Safari browser (including iOS WebKit),
 * where WebChannel streaming connections buffer or hang, necessitating forced long-polling.
 */
export function shouldUseForceLongPolling(
    ua = typeof navigator !== 'undefined' ? navigator.userAgent : '',
    platform = typeof navigator !== 'undefined' ? navigator.platform : '',
    maxTouchPoints = typeof navigator !== 'undefined' ? navigator.maxTouchPoints : 0
) {
    if (!ua && !platform) return false;
    const isMobile = /iPhone|iPad|iPod|Android|Mobile/i.test(ua) ||
        (platform === 'MacIntel' && maxTouchPoints > 1);
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    return isMobile || isSafari;
}

// Initialize services
let dbInstance;
try {
    const forceLongPolling = shouldUseForceLongPolling();
    dbInstance = initializeFirestore(app, {
        ...(forceLongPolling
            ? { experimentalForceLongPolling: true }
            : { experimentalAutoDetectLongPolling: true }),
        localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    });
} catch {
    dbInstance = getFirestore(app);
}
export const db = dbInstance;
export const auth = getAuth(app);
export { DEFAULT_TENANT_ID, getTenantCollection, getTenantDoc } from './tenant';

export const storage = getStorage(app);
export const functions = getFunctions(app);

// Firebase's fictional phone numbers use a mock reCAPTCHA during local testing.
// This must be configured before any RecaptchaVerifier is rendered and is never
// included in a production build.
if (import.meta.env.DEV) {
    auth.settings.appVerificationDisabledForTesting = true;
}

if (import.meta.env.VITE_USE_EMULATORS === 'true') {
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}

// Initialize App Check immediately without deferral so callable functions receive App Check tokens
if (typeof window !== 'undefined') {
    try {
        const debugTokenEnv = import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN;
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const isAppCheckDisabledForStaging = isStaging && import.meta.env.VITE_DISABLE_APPCHECK === 'true';
        if ((import.meta.env.DEV || isLocalhost) && !isAppCheckDisabledForStaging) {
            if (debugTokenEnv === 'disabled' || debugTokenEnv === 'false') {
                console.info("[App Check] Skipped in dev/localhost mode");
            } else if (debugTokenEnv) {
                self.FIREBASE_APPCHECK_DEBUG_TOKEN = debugTokenEnv;
            } else if (isStaging) {
                self.FIREBASE_APPCHECK_DEBUG_TOKEN = '5516fee4-bad9-4de0-a55d-4e27ee2ecd30';
            } else {
                self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
            }
        }
        if (!isAppCheckDisabledForStaging && (!import.meta.env.DEV || (debugTokenEnv !== 'disabled' && debugTokenEnv !== 'false'))) {
            initializeAppCheck(app, {
                provider: new ReCaptchaV3Provider(
                    isStaging
                        ? '6LcAShEtAAAAAKAk_-0L-yQZIjR8JfRcPN_5CK90'
                        : '6LcQ4XssAAAAAKjJpwGzqb9zC87k9ubxyPg9KjyW'
                ),
                isTokenAutoRefreshEnabled: true
            });
        }
    } catch (e) {
        console.warn("App Check initialization note:", e);
    }
}

import { setupConsentDefaults } from '../services/analyticsService';

// Initialize Analytics lazily without blocking first paint (AUD-066, Issue #223)
let analytics = null;
if (typeof window !== 'undefined') {
    setupConsentDefaults();
    if (import.meta.env.PROD) {
        const initAnalytics = () => {
            try {
                analytics = getAnalytics(app);
            } catch (e) {
                console.warn("Analytics initialization error:", e);
            }
        };
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(initAnalytics);
        } else {
            setTimeout(initAnalytics, 1000);
        }
    }
}
export { analytics };

import { signInWithCustomToken } from 'firebase/auth';

if (import.meta.env.DEV && typeof window !== 'undefined') {
    window.db = db;
    window.auth = auth;
    window.signInWithCustomToken = signInWithCustomToken;
}

export default app;
